import { FuelSpecValues, FuelTypePropertiesMap } from './fuel-spec-types';

export const FUEL_SPECS_STORE_ID = 'FuelSpecsStore';

const BANNED_FUEL_GRADES = ['B10LFO', 'B10MGO', 'BIOLFO', 'BIOMGO'];

export const ISO_FUEL_GRADE_MAP: Record<string, string[]> = {
  hfo: ['RME180', 'RMG180', 'RMG380', 'RMG500', 'RMG700', 'RMK380', 'RMK500', 'RMK700'],
  lfo: ['RMA10', 'RMB30', 'RMD80'],
  mgo: ['DMA', 'DMX', 'DFA', 'DMZ', 'DFZ', 'DMB', 'DFB'],
  mdo: ['DMA', 'DMX', 'DFA', 'DMZ', 'DFZ', 'DMB', 'DFB'],
  b10lfo: ['RMA10', 'RMB30', 'RMD80'],
  b10mgo: ['DMA', 'DMX', 'DFA', 'DMZ', 'DFZ', 'DMB', 'DFB'],
  biolfo: ['RMA10', 'RMB30', 'RMD80'],
  biomgo: ['DMA', 'DMX', 'DFA', 'DMZ', 'DFZ', 'DMB', 'DFB'],
  ulsfo2020: [
    'RMA10',
    'RMB30',
    'RMD80',
    'RME180',
    'RMG180',
    'RMG380',
    'RMG500',
    'RMG700',
    'RMK380',
    'RMK500',
    'RMK700',
    'DMA',
    'DMX',
    'DFA',
    'DMZ',
    'DFZ',
    'DMB',
  ],
  ulslfo2020: ['RMA10', 'RMB30', 'RMD80'],
  ulsmdo2020: ['DMA', 'DMX', 'DFA', 'DMZ', 'DFZ', 'DMB', 'DFB'],
  ulsmgo2020: ['DMA', 'DMX', 'DFA', 'DMZ', 'DFZ', 'DMB', 'DFB'],
  vlsfo2020: [
    'RMA10',
    'RMB30',
    'RMD80',
    'RME180',
    'RMG180',
    'RMG380',
    'RMG500',
    'RMG700',
    'RMK380',
    'RMK500',
    'RMK700',
    'DMA',
    'DMX',
    'DFA',
    'DMZ',
    'DFZ',
    'DMB',
  ],
  vlslfo2020: ['RMA10', 'RMB30', 'RMD80'],
  lpgp: ['LPG'],
  lpgb: ['LPG'],
  lng: ['LNG'],
  methanol: ['Methanol'],
  ethane: ['Ethane'],
  ethanol: ['Ethanol'],
  hvo: ['HVO'],
  fame: ['FAME'],
  biodiesel: ['Biodiesel'],
  bio_lng: ['Bio-LNG'],
  other: ['Other'],
} as const;

export const FILTERED_ISO_FUEL_GRADE_MAP: Record<string, string[]> = Object.fromEntries(
  Object.keys(ISO_FUEL_GRADE_MAP)
    .filter((fuelType) => !BANNED_FUEL_GRADES.includes(fuelType.toUpperCase()))
    .map((fuelType) => [fuelType, [...ISO_FUEL_GRADE_MAP[fuelType]].sort((a, b) => a.localeCompare(b))])
);

export const iso_fuel_type_specs = {
  DMX: {
    max_viscosity: 5.5,
    min_viscosity: 1.4,
    max_density: null,
    max_sulphur: 1.0,
    min_sulphur: null,
  },
  DMA: {
    max_viscosity: 6.0,
    min_viscosity: 2.0,
    max_density: 890,
    max_sulphur: 1.0,
    min_sulphur: null,
  },
  DFA: {
    max_viscosity: 6.0,
    min_viscosity: 2.0,
    max_density: 890,
    max_sulphur: 1.0,
    min_sulphur: null,
  },
  DMZ: {
    max_viscosity: 6.0,
    min_viscosity: 3.0,
    max_density: 890,
    max_sulphur: 1.0,
    min_sulphur: null,
  },
  DFZ: {
    max_viscosity: 6.0,
    min_viscosity: 3.0,
    max_density: 890,
    max_sulphur: 1.0,
    min_sulphur: null,
  },
  DMB: {
    max_viscosity: 11.0,
    min_viscosity: 2.0,
    max_density: 900,
    max_sulphur: 1.0,
    min_sulphur: null,
  },
  DFB: {
    max_viscosity: 11.0,
    min_viscosity: 2.0,
    max_density: 900,
    max_sulphur: 1.5,
    min_sulphur: null,
  },
  RMA10: {
    max_viscosity: 10.0,
    min_viscosity: null,
    max_density: 920,
    max_sulphur: null,
    min_sulphur: null,
  },
  RMB30: {
    max_viscosity: 30.0,
    min_viscosity: null,
    max_density: 960,
    max_sulphur: null,
    min_sulphur: null,
  },
  RMD80: {
    max_viscosity: 80.0,
    min_viscosity: null,
    max_density: 970,
    max_sulphur: null,
    min_sulphur: null,
  },
  RME180: {
    max_viscosity: 180.0,
    min_viscosity: null,
    max_density: 991,
    max_sulphur: null,
    min_sulphur: null,
  },
  RMG180: {
    max_viscosity: 180.0,
    min_viscosity: null,
    max_density: 991,
    max_sulphur: null,
    min_sulphur: null,
  },
  RMG380: {
    max_viscosity: 380.0,
    min_viscosity: null,
    max_density: 991,
    max_sulphur: null,
    min_sulphur: null,
  },
  RMG500: {
    max_viscosity: 500.0,
    min_viscosity: null,
    max_density: 991,
    max_sulphur: null,
    min_sulphur: null,
  },
  RMG700: {
    max_viscosity: 700.0,
    min_viscosity: null,
    max_density: 991,
    max_sulphur: null,
    min_sulphur: null,
  },
  RMK380: {
    max_viscosity: 380.0,
    min_viscosity: null,
    max_density: 1010,
    max_sulphur: null,
    min_sulphur: null,
  },
  RMK500: {
    max_viscosity: 500.0,
    min_viscosity: null,
    max_density: 1010,
    max_sulphur: null,
    min_sulphur: null,
  },
  RMK700: {
    max_viscosity: 700.0,
    min_viscosity: null,
    max_density: 1010,
    max_sulphur: null,
    min_sulphur: null,
  },
  LNG: {
    max_viscosity: null,
    min_viscosity: null,
    max_density: null,
    max_sulphur: null,
    min_sulphur: null,
  },
  LPG: {
    max_viscosity: null,
    min_viscosity: null,
    max_density: null,
    max_sulphur: null,
    min_sulphur: null,
  },
  Methanol: {
    max_viscosity: null,
    min_viscosity: null,
    max_density: null,
    max_sulphur: null,
    min_sulphur: null,
  },
  Ethanol: {
    max_viscosity: null,
    min_viscosity: null,
    max_density: null,
    max_sulphur: null,
    min_sulphur: null,
  },
};

export const fuelTypeProperties: FuelTypePropertiesMap = {
  hfo: {
    lcv: { min: 37000, max: 44000 },
    density: { min: 600, max: 1200 },
  },
  lfo: {
    lcv: { min: 37000, max: 44000 },
    density: { min: 600, max: 1200 },
  },
  mgo: {
    lcv: { min: 37000, max: 44000 },
    density: { min: 600, max: 1200 },
  },
  mdo: {
    lcv: { min: 37000, max: 44000 },
    density: { min: 600, max: 1200 },
  },
  b10lfo: {
    lcv: { min: 37000, max: 44000 },
    density: { min: 600, max: 1200 },
  },
  b10mgo: {
    lcv: { min: 37000, max: 44000 },
    density: { min: 600, max: 1200 },
  },
  biolfo: {
    lcv: { min: 37000, max: 44000 },
    density: { min: 600, max: 1200 },
  },
  biomgo: {
    lcv: { min: 37000, max: 44000 },
    density: { min: 600, max: 1200 },
  },
  ulsfo2020: {
    lcv: { min: 37000, max: 44000 },
    density: { min: 600, max: 1200 },
  },
  ulslfo2020: {
    lcv: { min: 37000, max: 44000 },
    density: { min: 600, max: 1200 },
  },
  ulsmdo2020: {
    lcv: { min: 37000, max: 44000 },
    density: { min: 600, max: 1200 },
  },
  ulsmgo2020: {
    lcv: { min: 37000, max: 44000 },
    density: { min: 600, max: 1200 },
  },
  vlsfo2020: {
    lcv: { min: 37000, max: 44000 },
    density: { min: 600, max: 1200 },
  },
  vlslfo2020: {
    lcv: { min: 37000, max: 44000 },
    density: { min: 600, max: 1200 },
  },
  ethane: {
    lcv: { min: 37000, max: 56000 },
    density: { min: 350, max: 800 },
  },
  lpgp: {
    lcv: { min: 37000, max: 56000 },
    density: { min: 350, max: 800 },
  },
  lpgb: {
    lcv: { min: 37000, max: 56000 },
    density: { min: 350, max: 800 },
  },
  lng: {
    lcv: { min: 37000, max: 56000 },
    density: { min: 350, max: 800 },
  },
  methanol: {
    lcv: { min: 20000, max: 44000 },
    density: { min: 600, max: 1200 },
  },
  ethanol: {
    lcv: { min: 20000, max: 44000 },
    density: { min: 600, max: 1200 },
  },
  hvo: {
    lcv: { min: 36000, max: 46000 },
    density: { min: 600, max: 1000 },
  },
  fame: {
    lcv: { min: 36000, max: 46000 },
    density: { min: 600, max: 1000 },
  },
  biodiesel: {
    lcv: { min: 20000, max: 44000 },
    density: { min: 600, max: 1000 },
  },
  bio_lng: {
    lcv: { min: 37000, max: 56000 },
    density: { min: 350, max: 800 },
  },
  other: {
    lcv: { min: 37000, max: 44000 },
    density: { min: 600, max: 1200 },
  },
};

// Ported from legacy V1's FUEL_DEFAULT_VALUES (app/utils/fuel-default-values.ts), used as a
// fallback when there's no live fuel-spec record for a grade — same source V1 used, since V1's
// autofill was never a live API lookup either. Keyed by fuel_grade alone: V1 also has an
// ISO-grade-keyed table, but it only covers vlsfo2020/ulsfo2020 and is value-identical to this
// table for both, so it adds nothing a fuel-grade-only fallback doesn't already cover.
// density is intentionally excluded — V1 never defaults it, for any fuel grade. sulphur/viscosity
// are only ever defaulted (to 0) for the gas-type grades below; V1 never defaults them for
// mgo/hfo/lfo/vlsfo2020/etc. c_slip, e_value and is_bio_fuel are also skipped: the first two are
// always null in V1, and is_bio_fuel has no equivalent field on FuelSpecValues (fuelCategoryForType
// already classifies biofuel grades on the wizard side).
export const FUEL_GRADE_DEFAULT_VALUES: Record<string, FuelSpecValues> = {
  hfo: { lcv: 40500, wttEmissionFactor: 13.5, cfCo2: 3.114, cfCh4: 0.00005, cfN2o: 0.00018 },
  lfo: { lcv: 41000, wttEmissionFactor: 13.2, cfCo2: 3.151, cfCh4: 0.00005, cfN2o: 0.00018 },
  mgo: { lcv: 42700, wttEmissionFactor: 14.4, cfCo2: 3.206, cfCh4: 0.00005, cfN2o: 0.00018 },
  mdo: { lcv: 42700, wttEmissionFactor: 14.4, cfCo2: 3.206, cfCh4: 0.00005, cfN2o: 0.00018 },
  ulsfo2020: { lcv: 40500, wttEmissionFactor: 13.5, cfCo2: 3.114, cfCh4: 0.00005, cfN2o: 0.00018 },
  ulslfo2020: { lcv: 41000, wttEmissionFactor: 13.2, cfCo2: 3.151, cfCh4: 0.00005, cfN2o: 0.00018 },
  ulsmdo2020: { lcv: 42700, wttEmissionFactor: 14.4, cfCo2: 3.206, cfCh4: 0.00005, cfN2o: 0.00018 },
  ulsmgo2020: { lcv: 42700, wttEmissionFactor: 14.4, cfCo2: 3.206, cfCh4: 0.00005, cfN2o: 0.00018 },
  vlsfo2020: { lcv: 40500, wttEmissionFactor: 13.5, cfCo2: 3.114, cfCh4: 0.00005, cfN2o: 0.00018 },
  vlslfo2020: { lcv: 41000, wttEmissionFactor: 13.2, cfCo2: 3.151, cfCh4: 0.00005, cfN2o: 0.00018 },
  ethane: {
    sulphur: 0,
    viscosity: 0,
    lcv: 46400,
    wttEmissionFactor: 18.5,
    cfCo2: 2.972,
    cfCh4: 0.00005,
    cfN2o: 0.00018,
  },
  lpgp: { sulphur: 0, viscosity: 0, lcv: 46000, wttEmissionFactor: 7.8, cfCo2: 3 },
  lpgb: { sulphur: 0, viscosity: 0, lcv: 46000, wttEmissionFactor: 7.8, cfCo2: 3.03 },
  lng: { sulphur: 0, viscosity: 0, lcv: 49100, wttEmissionFactor: 18.5, cfCo2: 2.75, cfCh4: 0, cfN2o: 0.00011 },
  methanol: { wttEmissionFactor: 31.3, cfCo2: 1.375 },
  ethanol: { cfCo2: 1.913 },
  hvo: { lcv: 44000, cfCo2: 3.115, cfCh4: 0.00005, cfN2o: 0.00018 },
  fame: { lcv: 37000, cfCo2: 2.834, cfCh4: 0.00005, cfN2o: 0.00018 },
  biodiesel: { lcv: 37000, cfCo2: 2.834, cfCh4: 0.00005, cfN2o: 0.00018 },
  bio_lng: { sulphur: 0, viscosity: 0, lcv: 50000, cfCo2: 2.75, cfCh4: 0, cfN2o: 0.00011 },
};
